import type { Company } from "@station/contracts";
import {
  Alert,
  type BreadcrumbItem,
  EmptyState,
  FormField,
  PageHeader,
  Select,
  SkeletonRows,
} from "@station/ui";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { Building2, Fuel } from "lucide-react";
import { type ReactElement, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { LazyChart } from "../../components/chart-lazy.js";
import { errorMessage } from "../../lib/errors.js";
import { useDocumentTitle } from "../../lib/hooks.js";
import { useTenantShape } from "../../lib/tenant-shape.js";
import { CompanyPicker, companySearchShape, useAccountingCompany } from "../accounting/company.js";
import { useCompanyStations } from "../accounting/shared.js";
import { useStationScope } from "../cashflow/shared.js";
import type { ChartOption } from "./chart-options.js";

/** Akaryakıt ekranlarının ortak arama parametreleri: şirket (defter) ve istasyon (operasyon). */
export const fuelSearchShape = {
  ...companySearchShape,
  station: z.string().uuid().optional().catch(undefined),
};

export interface FuelContext {
  companyId: string;
  company: Company;
}

/**
 * Akaryakıt ekranlarının ortak çerçevesi: başlık + ŞİRKET seçici (muhasebe / gider ekranlarıyla aynı
 * `?company=` parametresi). İstasyon seçimi sayfanın içindedir (`useFuelStation`), çünkü kimi ekranlarda
 * filtre çubuğunun içinde durur.
 */
export function FuelPage({
  title,
  description,
  breadcrumb,
  actions,
  children,
}: {
  title: string;
  description?: string;
  breadcrumb?: BreadcrumbItem[];
  actions?: ReactNode | ((context: FuelContext) => ReactNode);
  children: (context: FuelContext) => ReactNode;
}): ReactElement {
  const { t } = useTranslation();
  const accounting = useAccountingCompany();
  useDocumentTitle(title);
  const context = accounting.company
    ? { companyId: accounting.company.id, company: accounting.company }
    : null;
  return (
    <>
      <PageHeader
        title={title}
        {...(description ? { description } : {})}
        breadcrumb={[{ label: t("menu.groups.fuel") }, ...(breadcrumb ?? [{ label: title }])]}
        actions={
          <>
            <CompanyPicker accounting={accounting} />
            {context ? (typeof actions === "function" ? actions(context) : actions) : null}
          </>
        }
      />
      {accounting.error ? (
        <Alert tone="danger">{errorMessage(accounting.error)}</Alert>
      ) : accounting.isLoading ? (
        <SkeletonRows rows={6} />
      ) : context === null ? (
        <EmptyState
          headingLevel={2}
          icon={<Building2 />}
          title={t("accounting.company.noneTitle")}
          description={t("accounting.company.noneDescription")}
        />
      ) : (
        // Şirket değişince sayfanın yerel durumu (seçili tank, sekme, filtre) sıfırlanır.
        <div key={context.companyId} className="grid gap-4">
          {children(context)}
        </div>
      )}
    </>
  );
}

export interface FuelStationOption {
  id: string;
  code: string;
  name: string;
}

export interface FuelStationSelection {
  stations: FuelStationOption[];
  stationId: string | null;
  /** Tek istasyonlu kiracıda ve görünür tek istasyonda seçici çizilmez (arayüz sadeleşir). */
  showPicker: boolean;
  isLoading: boolean;
  error: unknown;
  select: (stationId: string) => void;
}

/**
 * Akaryakıt operasyonu İSTASYON bazındadır. Seçim URL'de taşınır (`?station=`), kapsamı kısıtlı
 * kullanıcıya yalnızca kendi istasyonları gösterilir ve tek istasyonlu kiracıda seçici gizlenir.
 */
export function useFuelStation(companyId: string): FuelStationSelection {
  const stations = useCompanyStations(companyId);
  const scope = useStationScope();
  const shape = useTenantShape();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { station?: string };
  const visible = (stations.data ?? []).filter(
    (station) => !scope.restricted || scope.allowedStationIds.includes(station.id),
  );
  const selected = visible.find((station) => station.id === search.station) ?? visible[0] ?? null;
  return {
    stations: visible,
    stationId: selected?.id ?? null,
    showPicker: visible.length > 1 && !shape.isSingleStation,
    isLoading: stations.isPending,
    error: stations.error,
    select: (stationId) =>
      void navigate({
        to: ".",
        search: ((previous: Record<string, unknown>) => ({ ...previous, station: stationId })) as never,
        replace: true,
      }),
  };
}

export function StationField({
  selection,
  className = "w-64",
}: {
  selection: FuelStationSelection;
  className?: string;
}): ReactElement | null {
  const { t } = useTranslation();
  if (!selection.showPicker) return null;
  return (
    <FormField label={t("fuel.station")} className={className}>
      {(control) => (
        <Select
          {...control}
          value={selection.stationId ?? ""}
          onChange={(event) => selection.select(event.target.value)}
        >
          {selection.stations.map((station) => (
            <option key={station.id} value={station.id}>
              {station.code} {station.name}
            </option>
          ))}
        </Select>
      )}
    </FormField>
  );
}

/** Hiç istasyon görünmüyorsa (kapsam ya da kurulum eksik) ekranın geri kalanı çizilmez. */
export function NoStations(): ReactElement {
  const { t } = useTranslation();
  return (
    <EmptyState
      icon={<Fuel />}
      title={t("fuel.noStationsTitle")}
      description={t("fuel.noStationsDescription")}
      action={
        <Link to="/ayarlar/istasyonlar" className="text-sm underline underline-offset-4">
          {t("menu.stations")}
        </Link>
      }
    />
  );
}

/**
 * Grafik + ZORUNLU tablo alternatifi (ADR 0012 §15). Tablo `<details>` içinde ama gerçek ve görünürdür
 * (sr-only değildir): grafiği okuyamayan da aynı sayıları satır satır görebilir. Kaydırılabilir kap
 * klavyeyle odaklanabilir (`tabIndex={0}`), aksi hâlde klavye kullanıcısı sağdaki sütunlara ulaşamaz.
 */
export function ChartWithTable({
  ariaLabel,
  option,
  height,
  tableLabel,
  children,
}: {
  ariaLabel: string;
  option: ChartOption;
  height?: number;
  tableLabel: string;
  children: ReactNode;
}): ReactElement {
  const { t } = useTranslation();
  return (
    <div className="grid min-w-0 gap-3 [&>*]:min-w-0">
      <LazyChart option={option} ariaLabel={ariaLabel} {...(height === undefined ? {} : { height })} />
      <details className="rounded-md border">
        <summary className="flex min-h-6 cursor-pointer items-center px-3 py-2 text-sm font-medium">
          {t("fuel.showDataTable")}
        </summary>
        <div className="overflow-x-auto border-t" tabIndex={0} role="group" aria-label={tableLabel}>
          {children}
        </div>
      </details>
    </div>
  );
}

/** Tablo başlığı hücresi — akaryakıt ekranlarındaki tablolar aynı görünümü paylaşır. */
export function Th({
  children,
  align = "left",
}: {
  children: ReactNode;
  align?: "left" | "right";
}): ReactElement {
  return (
    <th scope="col" className={`px-3 py-2 ${align === "right" ? "text-right" : "text-left"}`}>
      {children}
    </th>
  );
}
